/**
 * useICalFeeds Hook
 *
 * Manages iCal feeds on a listing: read/write icalFeeds array,
 * validate new feeds server-side, call sync callable, generate
 * icalId UUID for export URL.
 */

import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../services/firebase';

export interface ICalFeedData {
  name: string;
  url: string;
  lastSynced?: Date;
  lastError?: string | null;
  lastErrorAt?: Date | null;
}

interface RawFeed {
  name: string;
  url: string;
  lastSynced?: { toDate: () => Date };
  lastError?: string | null;
  lastErrorAt?: { toDate: () => Date } | null;
}

interface UseICalFeedsReturn {
  feeds: ICalFeedData[];
  icalId: string | null;
  exportUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  addFeed: (name: string, url: string) => Promise<void>;
  removeFeed: (index: number) => Promise<void>;
  syncNow: () => Promise<{ synced: number; errors?: string[] }>;
  generateExportUrl: () => Promise<string>;
}

function mapRawFeeds(rawFeeds: RawFeed[]): ICalFeedData[] {
  return rawFeeds.map((f) => ({
    name: f.name,
    url: f.url,
    lastSynced: f.lastSynced?.toDate?.() || undefined,
    lastError: f.lastError ?? null,
    lastErrorAt: f.lastErrorAt?.toDate?.() || null,
  }));
}

function serializeFeeds(feeds: ICalFeedData[]) {
  return feeds.map((f) => ({
    name: f.name,
    url: f.url,
    ...(f.lastSynced ? { lastSynced: f.lastSynced } : {}),
    ...(f.lastError != null ? { lastError: f.lastError } : {}),
    ...(f.lastErrorAt ? { lastErrorAt: f.lastErrorAt } : {}),
  }));
}

export function useICalFeeds(listingId: string): UseICalFeedsReturn {
  const [feeds, setFeeds] = useState<ICalFeedData[]>([]);
  const [icalId, setIcalId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);

  // Fetch feeds and icalId from listing doc
  useEffect(() => {
    if (!listingId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchFeeds() {
      try {
        const docRef = doc(db, 'listings', listingId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists() && !cancelled) {
          const data = docSnap.data();
          setFeeds(mapRawFeeds(data.icalFeeds || []));
          setIcalId(data.icalId || null);
        }
      } catch (err) {
        console.error('Error fetching iCal feeds:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchFeeds();
    return () => { cancelled = true; };
  }, [listingId]);

  const exportUrl = icalId
    ? `${window.location.origin}/api/calendar/${listingId}.ics?token=${icalId}`
    : null;

  const addFeed = useCallback(
    async (name: string, url: string) => {
      // Validate the feed server-side before saving: catches dead URLs,
      // non-iCal responses, and importing this listing's own export feed.
      const validateFn = httpsCallable<
        { listingId: string; url: string },
        { valid: boolean; eventCount: number; error?: string }
      >(functions, 'validateIcalFeed');
      const validation = await validateFn({ listingId, url });
      if (!validation.data.valid) {
        throw new Error(validation.data.error || 'Invalid iCal feed URL');
      }

      const newFeed: ICalFeedData = { name, url };
      const updatedFeeds: ICalFeedData[] = [...feeds, newFeed];

      const docRef = doc(db, 'listings', listingId);
      await updateDoc(docRef, { icalFeeds: serializeFeeds(updatedFeeds) });

      setFeeds(updatedFeeds);
    },
    [feeds, listingId]
  );

  const removeFeed = useCallback(
    async (index: number) => {
      const updatedFeeds = feeds.filter((_, i) => i !== index);

      const docRef = doc(db, 'listings', listingId);
      await updateDoc(docRef, { icalFeeds: serializeFeeds(updatedFeeds) });

      setFeeds(updatedFeeds);
    },
    [feeds, listingId]
  );

  const syncNow = useCallback(async () => {
    setIsSyncing(true);
    try {
      const syncFn = httpsCallable<{ listingId: string }, { synced: number; errors?: string[]; message: string }>(
        functions,
        'syncIcal'
      );
      const result = await syncFn({ listingId });

      // Refresh feeds to get updated lastSynced / lastError
      const docRef = doc(db, 'listings', listingId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setFeeds(mapRawFeeds(docSnap.data().icalFeeds || []));
      }

      return { synced: result.data.synced, errors: result.data.errors };
    } finally {
      setIsSyncing(false);
    }
  }, [listingId]);

  const generateExportUrl = useCallback(async () => {
    const newIcalId = crypto.randomUUID();
    const docRef = doc(db, 'listings', listingId);
    await updateDoc(docRef, { icalId: newIcalId });
    setIcalId(newIcalId);
    return `${window.location.origin}/api/calendar/${listingId}.ics?token=${newIcalId}`;
  }, [listingId]);

  return {
    feeds,
    icalId,
    exportUrl,
    isLoading,
    isSyncing,
    addFeed,
    removeFeed,
    syncNow,
    generateExportUrl,
  };
}
